import { useData } from '@/i18n/LangContext';

interface CvHeaderProps {
  name: string;
  title: string;
  email: string;
  location: string;
  website: string;
  phone?: string;
  nationality?: string;
  birthdate?: string;
  gender?: string;
}

export default function CvHeader({ 
  name = "Robin Wichmann", 
  title = "Fullstack-Frontend-Entwickler",
  email = "robwichmann@gmail.com", 
  location = "Hauptstr. 58c, 21640 Bliedersdorf", 
  website = "robinwichmann.com",
  phone = "0173 - 7126379",
}: Partial<CvHeaderProps>) {
  const { ui } = useData();
  return (
    <header className="mb-12 relative print:mb-6">
      {/* Name und Titel mit farbigem Hintergrund */}
      <div className="text-center pb-8 mb-8 relative">
        <h1 className="text-4xl md:text-5xl font-bold mb-2 text-white print:text-black">
          {name}
        </h1>
        
        <p className="text-xl text-[#C69C6D] font-light print:text-black print:mb-4">
          {title}
        </p>
        
        {/* Dekorativer Trennstrich */}
        <div className="absolute -bottom-4 left-1/2 transform -translate-x-1/2 w-32 h-[2px] bg-[#C69C6D] shadow-[0_0_10px_rgba(198,156,109,0.4)] print:bg-black print:opacity-50 print:shadow-none"></div>
      </div>
      
      {/* Kontaktinformationen in strukturiertem Raster */}
      <div className="bg-[rgba(198,156,109,0.05)] rounded-lg p-6 border border-[rgba(198,156,109,0.2)] print:bg-white print:border-gray-200 print:p-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 print:gap-3 print:grid-cols-2">
          {/* Linke Spalte */}
          <div className="space-y-3 print:space-y-2">
            <div className="flex items-center text-sm print:text-black">
              <div className="w-8 h-8 rounded-full bg-[rgba(198,156,109,0.15)] flex items-center justify-center mr-3 print:bg-gray-100">
                <svg className="w-4 h-4 flex-shrink-0 fill-[#C69C6D] print:fill-black" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                  <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 14H4V8l8 5 8-5v10zm-8-7L4 6h16l-8 5z"/>
                </svg>
              </div>
              <div>
                <div className="text-white/50 text-xs mb-0.5 print:text-gray-600">{ui.cv.emailLabel}</div>
                <div>{email}</div>
              </div>
            </div>
            
            <div className="flex items-center text-sm print:text-black">
              <div className="w-8 h-8 rounded-full bg-[rgba(198,156,109,0.15)] flex items-center justify-center mr-3 print:bg-gray-100">
                <svg className="w-4 h-4 flex-shrink-0 fill-[#C69C6D] print:fill-black" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                  <path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z"/>
                </svg>
              </div>
              <div>
                <div className="text-white/50 text-xs mb-0.5 print:text-gray-600">{ui.cv.phoneLabel}</div>
                <div>{phone}</div>
              </div>
            </div>
          </div>
          
          {/* Rechte Spalte */}
          <div className="space-y-3 print:space-y-2">
            <div className="flex items-center text-sm print:text-black">
              <div className="w-8 h-8 rounded-full bg-[rgba(198,156,109,0.15)] flex items-center justify-center mr-3 print:bg-gray-100">
                <svg className="w-4 h-4 flex-shrink-0 fill-[#C69C6D] print:fill-black" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                  <path d="M12 12c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm6-1.8C18 6.57 15.35 4 12 4s-6 2.57-6 6.2c0 2.34 1.95 5.44 6 9.14 4.05-3.7 6-6.8 6-9.14zM12 2c4.2 0 8 3.22 8 8.2 0 3.32-2.67 7.25-8 11.8-5.33-4.55-8-8.48-8-11.8C4 5.22 7.8 2 12 2z"/>
                </svg>
              </div>
              <div>
                <div className="text-white/50 text-xs mb-0.5 print:text-gray-600">{ui.cv.addressLabel}</div>
                <div>{location}</div>
              </div>
            </div>
            
            <div className="flex items-center text-sm print:text-black">
              <div className="w-8 h-8 rounded-full bg-[rgba(198,156,109,0.15)] flex items-center justify-center mr-3 print:bg-gray-100">
                <svg className="w-4 h-4 flex-shrink-0 fill-[#C69C6D] print:fill-black" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm-1-14h2v7h-2zm0 8h2v2h-2z"/>
                </svg>
              </div>
              <div>
                <div className="text-white/50 text-xs mb-0.5 print:text-gray-600">Website</div>
                <div>{website}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
} 